import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, ToggleGroup, ToggleGroupItem } from '@ibsys/kit';
import { STRATEGIST_CONCEPT_ROLE } from '@/entities/strategist';
import type { StrategistRowRole, StrategistTargetAttribute } from '@/entities/strategist';
import { TargetMetricPicker } from '@/features/strategist-target';
import { ThresholdSlider } from '@/shared/ui';

type Props = {
  /** Роль концептов-строк */
  rowRole: StrategistRowRole;
  /** Смена роли строк */
  onRowRoleChange: (next: StrategistRowRole) => void;
  /** Текущая целевая метрика */
  target: StrategistTargetAttribute;
  /** Смена целевой метрики */
  onTargetChange: (next: StrategistTargetAttribute) => void;
  /** Текущее число строк */
  topRows: number;
  /** Смена числа строк */
  onTopRowsChange: (next: number) => void;
  /** Текущее число столбцов */
  topCols: number;
  /** Смена числа столбцов */
  onTopColsChange: (next: number) => void;
  /** Доступные пороги близости (шаги ползунка) */
  thresholds: number[];
  /** Текущий порог близости строк */
  threshold: number | undefined;
  /** Смена порога близости */
  onThresholdChange: (next: number) => void;
};

/**
 * Панель контролов матрицы стандартов: переключатель роли строк
 * (возражения клиента / обработка оператора), выбор целевой метрики,
 * ограничение числа строк/столбцов (топ по частоте) и один ползунок порога
 * близости для строк — подпись уточняется по текущей роли строк.
 */
export const StrategistStandardsControls: FC<Props> = ({
  rowRole,
  onRowRoleChange,
  target,
  onTargetChange,
  topRows,
  onTopRowsChange,
  topCols,
  onTopColsChange,
  thresholds,
  threshold,
  onThresholdChange,
}) => {
  const { t } = useTranslation('strategist');

  // Подпись порога зависит от роли строк: возражения или аргументы.
  const thresholdLabel =
    rowRole === STRATEGIST_CONCEPT_ROLE.objection
      ? t('standards.thresholdObjection')
      : t('standards.thresholdArgument');

  return (
    <div className="flex w-full flex-wrap items-end gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-xs text-muted-foreground">{t('standards.rowRole.label')}</span>
        <ToggleGroup value={rowRole} onValueChange={(v: string) => v && onRowRoleChange(v as StrategistRowRole)}>
          <ToggleGroupItem value="возражение" className="!flex-none whitespace-nowrap px-4">
            {t('standards.rowRole.objection')}
          </ToggleGroupItem>
          <ToggleGroupItem value="аргумент" className="!flex-none whitespace-nowrap px-4">
            {t('standards.rowRole.argument')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <TargetMetricPicker value={target} onChange={onTargetChange} />
      <Input
        type="number"
        label={t('standards.controls.topRows')}
        value={String(topRows)}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n) && n > 0) onTopRowsChange(n);
        }}
        className="w-20"
      />
      <Input
        type="number"
        label={t('standards.controls.topCols')}
        value={String(topCols)}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n) && n > 0) onTopColsChange(n);
        }}
        className="w-20"
      />
      <ThresholdSlider
        label={thresholdLabel}
        thresholds={thresholds}
        value={threshold}
        onChange={onThresholdChange}
        coarserLabel={t('threshold.coarser')}
        finerLabel={t('threshold.finer')}
      />
    </div>
  );
};
